// Operator notification + acceptance surface — HTTP calls to the operator-bff
// (N10). All requests ride the shared `http` (ky) client, which injects the
// bearer token + X-Session-Urn + dev override (lib/http.ts). Routes per the N10
// controllers (`notifications.controller.ts` / `force-accept.controller.ts`).

import { http } from '@/lib/http';
import type {
  IForceAcceptBody,
  IForceAcceptResult,
  INotificationCaseStatus,
  ISendNotificationBody,
  ISendNotificationResult,
} from './types';
import { newIdempotencyKey } from '@/lib/uuid';

/** Per-PNR-group delivery + acceptance status for a case. `GET /v1/notifications/case/:case_urn`. */
export async function getCaseNotifications(caseUrn: string): Promise<INotificationCaseStatus> {
  return http
    .get(`v1/notifications/case/${encodeURIComponent(caseUrn)}`)
    .json<INotificationCaseStatus>();
}

/**
 * Send the current PNR state / choose its channel. `POST /v1/notifications/:case_urn/send`.
 * Omit `subCaseUrn` to (re-)dispatch the whole case; omit `channel` to honour the
 * group preference / template default. 202 `{ status: 'queued' | 'unavailable' }`.
 */
export async function resendNotification(
  caseUrn: string,
  body: ISendNotificationBody = {},
): Promise<ISendNotificationResult> {
  return http
    .post(`v1/notifications/${encodeURIComponent(caseUrn)}/send`, {
      json: body,
      timeout: 20_000,
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<ISendNotificationResult>();
}

/**
 * Per-PNR accept-on-behalf (AIRPORT_OPERATOR+ — the Express counter flow; the
 * BFF enforces the gate). `POST /v1/cases/:case_urn/sub-cases/:subCaseUrn/
 * force-accept`. Produces the SAME canonical OfferAccepted downstream
 * (`source: OPERATOR_FORCE`, `forced: true`, actor = the operator). The MASS
 * variants (`force-accept-all`, disruption-wide) stay supervisor-gated.
 */
export async function forceAccept(
  caseUrn: string,
  subCaseUrn: string,
  body: IForceAcceptBody,
): Promise<IForceAcceptResult> {
  return http
    .post(
      `v1/cases/${encodeURIComponent(caseUrn)}/sub-cases/${encodeURIComponent(
        subCaseUrn,
      )}/force-accept`,
      { json: body },
    )
    .json<IForceAcceptResult>();
}

/** Tally of a case-wide force-accept (`POST /v1/cases/:case_urn/force-accept-all`). */
export interface IForceAcceptAllResult {
  caseUrn: string;
  total: number;
  forced: number;
  alreadyResponded: number;
  noOffer: number;
  failed: number;
  failedSubCaseUrns: string[];
}

/**
 * Operator "Aceptar todo" (supervisor+; the BFF + cases enforce the role gate).
 * Forces every PNR group still awaiting acceptance with a live offer — each one
 * produces the SAME canonical OfferAccepted as the per-PNR force.
 */
export async function forceAcceptAll(
  caseUrn: string,
  body: { reason?: string } = {},
): Promise<IForceAcceptAllResult> {
  return http
    .post(`v1/cases/${encodeURIComponent(caseUrn)}/force-accept-all`, {
      json: body,
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    })
    .json<IForceAcceptAllResult>();
}

/** What the passenger-app link is for: hand it to the passenger, or see the app as they do. */
export type PaxLinkPurpose = 'SHARE' | 'PREVIEW';

/** The passenger-app link of one PNR (`operator-bff:POST …/pnrs/:sub/pax-link`). A bearer link: never stored. */
export interface IPaxLink {
  url: string;
  purpose: PaxLinkPurpose;
  expiresAt: string;
}

/**
 * The passenger card's «App del pasajero» (2026-11-03). `SHARE` — the passenger's own link
 * (48 h) to send another way or read out; `PREVIEW` — a 10-minute link that opens their app
 * read-only and records nothing on the card. AIRPORT_OPERATOR+; the BFF scopes the PNR to
 * the disruption and audits who asked.
 */
export async function requestPaxLink(
  disruptionUrn: string,
  subCaseUrn: string,
  caseUrn: string,
  purpose: PaxLinkPurpose,
): Promise<IPaxLink> {
  return http
    .post(
      `v1/disruptions/${encodeURIComponent(disruptionUrn)}/pnrs/${encodeURIComponent(subCaseUrn)}/pax-link`,
      { json: { caseUrn, purpose }, timeout: 15_000 },
    )
    .json<IPaxLink>();
}
